import { validatePhone, validateZip, validateEmail } from './validateInputs';
import { MultiQuestion, Question, Step } from '../types/types';
import {
  FlowState,
  ValidationError,
  ValidationResult,
} from '../providers/dynamicExperienceReducer';
import { StepType } from '../types/enums';

type ResolvedZipData = ValidationResult['resolvedZipData'];

// Check what type of validation a step needs based on title content
export const stepNeedsValidation = (
  step: Step,
): { needsPhone: boolean; needsZip: boolean; needsEmail: boolean } => {
  let needsPhone: boolean = false;
  let needsZip: boolean = false;
  let needsEmail: boolean = false;

  if (step.type === StepType.MULTI_QUESTION) {
    const stepQuestions = (step as MultiQuestion).stepQuestions || [];
    needsPhone = stepQuestions.some(
      (sq: any) => sq.leadFieldAssociated === 'phoneNumber',
    );
    needsZip = stepQuestions.some(
      (sq: any) => sq.leadFieldAssociated === 'zip',
    );
    needsEmail = stepQuestions.some(
      (sq: any) => sq.leadFieldAssociated === 'email',
    );
  } else {
    needsPhone = (step.leadFieldAssociated as string) === 'phoneNumber';
    needsZip = (step.leadFieldAssociated as string) === 'zip';
    needsEmail = (step.leadFieldAssociated as string) === 'email';
  }

  return {
    needsPhone,
    needsZip,
    needsEmail,
  };
};

const fieldValidation = async (
  value: string,
  fieldType: string,
): Promise<{
  errors: ValidationError[];
  resolvedZipData?: ResolvedZipData;
}> => {
  const errors: ValidationError[] = [];
  let resolvedZipData: ResolvedZipData;

  if (fieldType === 'phoneNumber') {
    if (value.trim().length === 0) {
      errors.push({
        field: fieldType,
        message: 'Please enter a phone number',
      });
    } else {
      const phoneResult = await validatePhone(value);

      if (!phoneResult?.validPhoneNumber) {
        errors.push({
          field: fieldType,
          message: 'Please enter a valid phone number',
        });
      }
    }
  }

  if (fieldType === 'zip') {
    if (value.trim().length === 0) {
      errors.push({
        field: fieldType,
        message: 'Please enter a ZIP code',
      });
    }
    const zipResult = await validateZip(value);
    if (!zipResult?.validZip) {
      errors.push({
        field: fieldType,
        message: 'Please enter a valid ZIP code',
      });
    } else if ('stateAbbr' in zipResult) {
      resolvedZipData = {
        stateAbbr: zipResult.stateAbbr,
        state: zipResult.state,
        city: zipResult.city,
      };
    }
  }

  if (fieldType === 'email') {
    if (value.trim().length === 0) {
      errors.push({
        field: fieldType,
        message: 'Please enter an email address',
      });
    } else if (!validateEmail(value)) {
      errors.push({
        field: fieldType,
        message: 'Please enter a valid email address',
      });
    }
  }
  return { errors, resolvedZipData };
};

// Validate current step data when user tries to proceed
export const validateCurrentStep = async (
  step: Step,
  flowState: FlowState,
): Promise<ValidationResult> => {
  const errors: ValidationError[] = [];
  let resolvedZipData: ResolvedZipData;
  const stepData = flowState[step.id || ''];

  // Get validation requirements for this step
  const validationNeeds = stepNeedsValidation(step);

  // If step doesn't need validation, skip it
  if (
    !validationNeeds.needsPhone &&
    !validationNeeds.needsZip &&
    !validationNeeds.needsEmail
  ) {
    return { isValid: true, errors: [] };
  }

  // If step needs validation but has no data, it's invalid (empty required field)
  if (!stepData?.option) {
    const fieldType = step.title?.toString().toLowerCase().includes('phone')
      ? 'phone number'
      : step.title?.toString().toLowerCase().includes('zip')
        ? 'ZIP code'
        : step.title?.toString().toLowerCase().includes('email')
          ? 'email address'
          : 'field';
    return {
      isValid: false,
      errors: [
        {
          field: (step.leadFieldAssociated as string) || step.id || 'unknown',
          message: `Please enter a ${fieldType}`,
        },
      ],
    };
  }

  // For multi-questions, validate each option based on its specific sub-question context
  if (step.type === StepType.MULTI_QUESTION) {
    const stepQuestions = (step as any).stepQuestions || [];

    for (let i = 0; i < stepQuestions.length; i++) {
      const subQuestion = stepQuestions[i];
      const subQuestionData = flowState[subQuestion.id || ''];

      // Check what validation this specific sub-question needs using array index
      const needsPhoneValidation =
        subQuestion.leadFieldAssociated === 'phoneNumber';
      const needsZipValidation = subQuestion.leadFieldAssociated === 'zip';
      const needsEmailValidation = subQuestion.leadFieldAssociated === 'email';

      // If this sub-question needs validation but has no data, add error
      if (
        (needsPhoneValidation || needsZipValidation || needsEmailValidation) &&
        !subQuestionData?.option
      ) {
        const fieldType = needsPhoneValidation
          ? 'phone number'
          : needsZipValidation
            ? 'ZIP code'
            : 'email address';
        errors.push({
          field: subQuestion.leadFieldAssociated || subQuestion.id || 'unknown',
          message: `Please enter a ${fieldType}`,
        });
      }

      if (!subQuestionData?.option) continue;

      const subOptions = Array.isArray(subQuestionData.option)
        ? subQuestionData.option
        : [subQuestionData.option];

      for (const option of subOptions) {
        const value = option.value?.toString() || '';
        const result = await fieldValidation(
          value,
          subQuestion.leadFieldAssociated,
        );
        if (result.errors) {
          errors.push(...result.errors);
        }
        if (result.resolvedZipData) {
          resolvedZipData = result.resolvedZipData;
        }
      }
    }
  } else {
    // For single questions, use the original logic
    const options = Array.isArray(stepData.option)
      ? stepData.option
      : [stepData.option];

    for (const option of options) {
      const value = option.value?.toString() || '';
      const leadField = stepData?.leadFieldAssociated as string;
      const result = await fieldValidation(value, leadField);
      if (result.errors) {
        errors.push(...result.errors);
      }
      if (result.resolvedZipData) {
        resolvedZipData = result.resolvedZipData;
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    resolvedZipData,
  };
};
